/**
 * useDialogHandlers — extraído de ForecastPage.tsx (P7 split).
 *
 * Consolida los handlers de diálogos: open/close de inventory/sales/detail,
 * actions menu, calc dialog close, confirm dialog close/confirm.
 */

import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
} from "react";

interface UseDialogHandlersOptions {
  setSelectedProductId: Dispatch<SetStateAction<string | null>>;
  setSelectedProductName: Dispatch<SetStateAction<string | undefined>>;
  setInventoryDialogOpen: Dispatch<SetStateAction<boolean>>;
  setSalesDialogOpen: Dispatch<SetStateAction<boolean>>;
  setDetailDialogOpen: Dispatch<SetStateAction<boolean>>;
  setSelectedSupplierId: Dispatch<SetStateAction<number | null>>;
  setSupplierSelectLoading: Dispatch<SetStateAction<boolean>>;
  setActionsAnchorEl: Dispatch<SetStateAction<HTMLElement | null>>;
  setSelectedIds: Dispatch<SetStateAction<Set<string>>>;
  setSelectedSupplierEstimatedAmount: Dispatch<
    SetStateAction<number | null>
  >;
  setHorizonAddedIds: Dispatch<SetStateAction<Set<string>>>;
  setDialogSelectionSyncLoading: Dispatch<SetStateAction<boolean>>;
  setCalcDialogOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmDialogOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmAction: Dispatch<
    SetStateAction<"activate" | "deactivate" | null>
  >;
  restoreSelectionOnDialogCloseRef: MutableRefObject<boolean>;
  dialogBaseSelectedIds: Set<string>;
  dialogBaseEstimatedAmount: number | null;
}

export function useDialogHandlers(opts: UseDialogHandlersOptions): {
  handleOpenInventory: (id: string, displayName: string) => void;
  handleOpenSales: (id: string, displayName: string) => void;
  handleOpenDetail: (id: string, displayName: string) => void;
  handleSelectSupplier: (id: number, name: string) => Promise<void>;
  handleOpenActionsMenu: (e: React.MouseEvent<HTMLElement>) => void;
  handleCloseActionsMenu: () => void;
  handleCalcDialogClose: () => void;
  handleConfirmDialogClose: () => void;
} {
  const {
    setSelectedProductId,
    setSelectedProductName,
    setInventoryDialogOpen,
    setSalesDialogOpen,
    setDetailDialogOpen,
    setSelectedSupplierId,
    setSupplierSelectLoading,
    setActionsAnchorEl,
    setSelectedIds,
    setSelectedSupplierEstimatedAmount,
    setHorizonAddedIds,
    setDialogSelectionSyncLoading,
    setCalcDialogOpen,
    setConfirmDialogOpen,
    setConfirmAction,
    restoreSelectionOnDialogCloseRef,
    dialogBaseSelectedIds,
    dialogBaseEstimatedAmount,
  } = opts;

  const handleOpenInventory = useCallback(
    (id: string, displayName: string) => {
      setSelectedProductId(id);
      setSelectedProductName(displayName);
      setInventoryDialogOpen(true);
    },
    [],
  );

  const handleOpenSales = useCallback(
    (id: string, displayName: string) => {
      setSelectedProductId(id);
      setSelectedProductName(displayName);
      setSalesDialogOpen(true);
    },
    [],
  );

  const handleOpenDetail = useCallback(
    (id: string, displayName: string) => {
      setSelectedProductId(id);
      setSelectedProductName(displayName);
      setDetailDialogOpen(true);
    },
    [],
  );

  const handleSelectSupplier = useCallback(
    async (supplierId: number, _supplierName: string) => {
      setSelectedSupplierId(supplierId);
      setSupplierSelectLoading(false);
    },
    [],
  );

  const handleOpenActionsMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>) => setActionsAnchorEl(e.currentTarget),
    [],
  );
  const handleCloseActionsMenu = useCallback(
    () => setActionsAnchorEl(null),
    [],
  );

  const handleCalcDialogClose = useCallback(() => {
    if (restoreSelectionOnDialogCloseRef.current) {
      setSelectedIds(new Set(dialogBaseSelectedIds));
      setSelectedSupplierEstimatedAmount(dialogBaseEstimatedAmount);
    }
    setHorizonAddedIds(new Set());
    setDialogSelectionSyncLoading(false);
    setCalcDialogOpen(false);
    if (restoreSelectionOnDialogCloseRef) {
      restoreSelectionOnDialogCloseRef.current = true;
    }
  }, [dialogBaseEstimatedAmount, dialogBaseSelectedIds]);

  const handleConfirmDialogClose = useCallback(() => {
    setConfirmDialogOpen(false);
    setConfirmAction(null);
  }, []);

  return {
    handleOpenInventory,
    handleOpenSales,
    handleOpenDetail,
    handleSelectSupplier,
    handleOpenActionsMenu,
    handleCloseActionsMenu,
    handleCalcDialogClose,
    handleConfirmDialogClose,
  };
}
